import React, { useState, useEffect } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress"; // Material UI spinner
import { getUserCredits } from "../../api/businesses";

interface FormFillingProps {
  onProcessPhoto: (
    roomType: string,
    furnitureStyle: string,
    images: File[]
  ) => void;
  isProcessing: boolean;
}

const FormFilling: React.FC<FormFillingProps> = ({
  onProcessPhoto,
  isProcessing,
}) => {
  const [roomType, setRoomType] = useState("");
  const [furnitureStyle, setFurnitureStyle] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [remainingCredits, setRemainingCredits] = useState<number | null>(null);

  // Messages to show in sequence while processing
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const loadingMessages = [
    "Preparing your image...",
    "Applying style adjustments...",
    "Please wait, almost done...",
  ];

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isProcessing) {
      // Move to the next message every 10 seconds, but stop at the last one
      interval = setInterval(() => {
        setLoadingMessageIndex((prevIndex) => {
          if (prevIndex < loadingMessages.length - 1) {
            return prevIndex + 1;
          }
          return prevIndex; // stay on the last message
        });
      }, 10000);
    } else {
      // Reset index when not processing
      setLoadingMessageIndex(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing, loadingMessages.length]);

  const handleRoomTypeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRoomType(event.target.value);
  };

  const handleFurnitureStyleChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setFurnitureStyle(event.target.value);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setImage(event.target.files[0]);
    }
  };

  const handleClearImage = () => {
    setImage(null);
  };

  const handleProcess = async () => {
    if (!roomType || !furnitureStyle || !image) {
      alert("Please select a room type, furniture style, and upload an image.");
      return;
    }

    try {
      const res = await getUserCredits();
      const credits = res.data.credits;

      if (credits <= 0) {
        alert(
          "You don't have enough credits to process a photo. Please add more credits."
        );
        return;
      }

      setRemainingCredits(credits);
      onProcessPhoto(roomType, furnitureStyle, [image]);
    } catch (error) {
      console.error("Error fetching user credits:", error);
      alert("Failed to fetch your credits. Please try again later.");
    }
  };

  const allConditionsMet = roomType && furnitureStyle && image;

  return (
    <div className="w-full max-w-md bg-white p-4 rounded-lg shadow-lg border">
      <div className="mb-4">
        <label className="block text-gray-600 mb-2">Room Type</label>
        <select
          value={roomType}
          onChange={handleRoomTypeChange}
          className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
        >
          <option value="">Select Room Type</option>
          <option value="living">Living Room</option>
          <option value="bed">Bedroom</option>
          <option value="kitchen">Kitchen</option>
          <option value="dining">Dining Room</option>
          <option value="home_office">Office</option>
          <option value="outdoor">Outdoor</option>
          <option value="kids_room">Kids Room</option>
        </select>
      </div>

      <div className="mb-4">
        <label className="block text-gray-600 mb-2">Furniture Style</label>
        <select
          value={furnitureStyle}
          onChange={handleFurnitureStyleChange}
          className="w-full p-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
        >
          <option value="">Select Furniture Style</option>
          <option value="modern">Modern</option>
          <option value="scandinavian">Scandinavian</option>
          <option value="industrial">Industrial</option>
          <option value="midcentury">Midcentury</option>
          <option value="luxury">Luxury</option>
          <option value="farmhouse">Farmhouse</option>
          <option value="coastal">Coastal</option>
          <option value="standard">Standard</option>
        </select>
      </div>

      <div className="w-full h-64 border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4 flex justify-center items-center relative">
        {image ? (
          <div className="relative w-full h-full">
            <img
              src={URL.createObjectURL(image)}
              alt="Uploaded"
              className="w-full h-full object-cover rounded-md"
            />
            <button
              onClick={handleClearImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
            >
              <DeleteIcon fontSize="small" />
            </button>
          </div>
        ) : (
          <label
            htmlFor="upload"
            className="text-center flex flex-col items-center cursor-pointer"
          >
            <UploadFileIcon fontSize="large" />
            <span>Upload Image</span>
          </label>
        )}
        <input
          id="upload"
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
      </div>

      <button
        onClick={handleProcess}
        disabled={!allConditionsMet || isProcessing}
        className={`w-full py-2 px-4 ${
          allConditionsMet && !isProcessing
            ? "bg-blue-500 text-white"
            : "bg-gray-300 text-gray-500"
        } rounded-lg flex justify-center items-center`}
      >
        {isProcessing ? (
          <div className="flex items-center">
            <CircularProgress size={20} color="inherit" />
            <span className="ml-2">{loadingMessages[loadingMessageIndex]}</span>
          </div>
        ) : (
          "Process Photo"
        )}
      </button>
    </div>
  );
};

export default FormFilling;
